import React, { useState, useEffect, useCallback } from 'react';
import { apiEndpoints, handleApiError } from '../services/api';
import {
  TrendingUp, Building2, Package, CreditCard, DollarSign, RefreshCw, FileText, CheckCircle, Clock, AlertCircle
} from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('fr-DZ', { style: 'currency', currency: 'DZD', maximumFractionDigits: 0 }).format(n ?? 0);

const InventoryReporting = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);

  const loadReportingData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, suppRes, invRes, itemRes] = await Promise.all([
        apiEndpoints.supplierInvoices.getSummary(),
        apiEndpoints.suppliers.getAll(),
        apiEndpoints.supplierInvoices.getAll({ limit: 500 }),
        apiEndpoints.inventory.getAll({ limit: 500 })
      ]);

      setSummary(sumRes.data);
      setSuppliers(suppRes.data || []);
      setInvoices(invRes.data?.items || []);

      const rawItems = itemRes.data;
      setInventoryItems(Array.isArray(rawItems) ? rawItems : (rawItems?.items || []));
    } catch (err) {
      handleApiError(err, 'Erreur lors du chargement du reporting');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReportingData();
  }, [loadReportingData]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-gray-400 gap-3">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
        <span className="text-sm">Chargement du bilan & reporting global...</span>
      </div>
    );
  }

  // Active (non-cancelled) invoices for accurate financial reporting
  const activeInvoices = invoices.filter(i => i.status !== 'Cancelled' && i.status !== 3);

  // Global calculations (excluding cancelled invoices)
  const grandTotalPurchases = activeInvoices.reduce((acc, i) => acc + (i.totalAmount || 0), 0);
  const grandTotalPaid = activeInvoices.reduce((acc, i) => acc + (i.paidAmount || 0), 0);
  const grandTotalRemaining = activeInvoices.reduce((acc, i) => acc + (i.remainingBalance || 0), 0);
  const totalStockValue = inventoryItems.reduce((acc, item) => acc + ((item.currentQuantity || 0) * (item.unitCost || 0)), 0);

  // Per supplier reporting breakdown (excluding cancelled invoices)
  const supplierBreakdown = suppliers.map(sup => {
    const supInvoices = activeInvoices.filter(i => i.supplierId === sup.id);
    const total = supInvoices.reduce((a, b) => a + (b.totalAmount || 0), 0);
    const paid = supInvoices.reduce((a, b) => a + (b.paidAmount || 0), 0);
    const remaining = supInvoices.reduce((a, b) => a + (b.remainingBalance || 0), 0);
    return {
      supplier: sup,
      invoiceCount: supInvoices.length,
      total,
      paid,
      remaining
    };
  }).sort((a, b) => b.remaining - a.remaining); // Highest remaining debt on top

  return (
    <div className="p-6 space-y-6">
      {/* Top Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="h-7 w-7 text-indigo-600" />
            Reporting Global & Bilan Financier
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Aperçu général des créances, valeur du stock, dettes fournisseurs et statistiques d'achat.
          </p>
        </div>
        <button
          onClick={loadReportingData}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-sm font-medium transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Actualiser
        </button>
      </div>

      {/* ── Global Reporting Cards (ON TOP) ─────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 border-t-4 border-t-red-500">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total Dettes Dues</p>
              <h3 className="text-2xl font-extrabold text-red-600 mt-1">{fmt(grandTotalRemaining)}</h3>
            </div>
            <div className="p-2 bg-red-50 rounded-lg text-red-600">
              <AlertCircle className="h-6 w-6" />
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-3">Montant restant à régler aux fournisseurs</p>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 border-t-4 border-t-emerald-500">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total Règlements Effectués</p>
              <h3 className="text-2xl font-extrabold text-emerald-600 mt-1">{fmt(grandTotalPaid)}</h3>
            </div>
            <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600">
              <CheckCircle className="h-6 w-6" />
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-3">Somme des paiements versés (Acomptes)</p>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 border-t-4 border-t-indigo-500">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Volume Total d'Achats</p>
              <h3 className="text-2xl font-extrabold text-indigo-600 mt-1">{fmt(grandTotalPurchases)}</h3>
            </div>
            <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
              <CreditCard className="h-6 w-6" />
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-3">Total cumulé de toutes les factures</p>
        </div>
      </div>

      {/* ── Detailed Reporting per Supplier Table ───────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Building2 className="h-5 w-5 text-indigo-600" />
          Bilan Détaillé par Fournisseur
        </h3>

        {supplierBreakdown.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            Aucun fournisseur avec des factures enregistrées
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left font-bold text-gray-600 uppercase text-xs">Fournisseur</th>
                  <th className="px-5 py-3 text-center font-bold text-gray-600 uppercase text-xs">Factures</th>
                  <th className="px-5 py-3 text-right font-bold text-gray-600 uppercase text-xs">Volume Achats</th>
                  <th className="px-5 py-3 text-right font-bold text-gray-600 uppercase text-xs">Total Payé</th>
                  <th className="px-5 py-3 text-right font-bold text-gray-600 uppercase text-xs">Reste Dû (Dette)</th>
                  <th className="px-5 py-3 text-center font-bold text-gray-600 uppercase text-xs">Statut de Règlement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {supplierBreakdown.map((item, idx) => {
                  const pctPaid = item.total > 0 ? Math.round((item.paid / item.total) * 100) : 100;
                  return (
                    <tr key={idx} className="hover:bg-gray-50/80 transition-colors">
                      <td className="px-5 py-3.5 font-bold text-gray-900">
                        {item.supplier.name}
                        {item.supplier.vendorName && <span className="text-xs text-gray-500 font-normal block">{item.supplier.vendorName}</span>}
                      </td>
                      <td className="px-5 py-3.5 text-center font-semibold text-gray-600">
                        {item.invoiceCount}
                      </td>
                      <td className="px-5 py-3.5 text-right font-semibold text-gray-800">
                        {fmt(item.total)}
                      </td>
                      <td className="px-5 py-3.5 text-right font-semibold text-emerald-600">
                        {fmt(item.paid)}
                      </td>
                      <td className={`px-5 py-3.5 text-right font-extrabold ${item.remaining > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {fmt(item.remaining)}
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                          item.remaining === 0
                            ? 'bg-emerald-100 text-emerald-800'
                            : item.paid > 0
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-red-100 text-red-800'
                        }`}>
                          {item.remaining === 0 ? '✓ Soldé' : `${pctPaid}% réglé`}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryReporting;
